import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, Check, Star, Loader2, Pencil, LayoutTemplate } from "lucide-react";
import api from "@/lib/api";

export default function ProposalTemplatesList() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [cloneFrom, setCloneFrom] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proposal-templates");
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    if (!newName.trim()) { toast.error("Indique um nome"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/proposal-templates", {
        name: newName.trim(), description: newDesc.trim(),
        clone_from_id: cloneFrom || undefined,
      });
      toast.success("Template criado");
      setShowNew(false); setNewName(""); setNewDesc(""); setCloneFrom("");
      nav(`/settings/proposal-builder/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar");
    } finally { setCreating(false); }
  };

  const setDefault = async (id) => {
    try {
      await api.post(`/proposal-templates/${id}/set-default`);
      toast.success("Template definido como default");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminar template "${t.name}"?`)) return;
    try {
      await api.delete(`/proposal-templates/${t.id}`);
      toast.success("Template eliminado");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao eliminar"); }
  };

  const duplicate = async (t) => {
    try {
      const { data } = await api.post("/proposal-templates", {
        name: `${t.name} (cópia)`,
        description: t.description,
        clone_from_id: t.id,
      });
      toast.success("Template duplicado");
      nav(`/settings/proposal-builder/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-1">Configuração global</div>
            <h1 className="font-display text-3xl text-primary">Templates de Proposta</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Crie templates de proposta por segmento (ex: Standard, Caring Select premium). O template default é usado quando o Gestor não escolhe um específico no lead.
            </p>
          </div>
          <button onClick={() => setShowNew(true)}
            className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
            data-testid="new-template-btn">
            <Plus className="w-4 h-4" /> Novo template
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> A carregar...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map((t) => (
              <div key={t.id}
                className={`bg-card rounded-2xl border p-6 transition-shadow hover:shadow-md ${t.is_default ? "border-[hsl(var(--warning))] shadow-sm" : "border-border"}`}
                data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <LayoutTemplate className="w-6 h-6 text-primary" />
                  {t.is_default && (
                    <span className="text-[9px] uppercase tracking-wider bg-[hsl(var(--warning))] text-primary-foreground px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1">
                      <Star className="w-3 h-3" fill="white" /> Default
                    </span>
                  )}
                </div>
                <div className="font-display text-xl text-primary mb-1">{t.name}</div>
                {t.description && <div className="text-xs text-muted-foreground mb-4 line-clamp-2">{t.description}</div>}
                {t.updated_at && (
                  <div className="text-[10px] text-muted-foreground mb-4">
                    Atualizado {new Date(t.updated_at).toLocaleDateString("pt-PT")}
                    {t.updated_by_name && ` por ${t.updated_by_name}`}
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => nav(`/settings/proposal-builder/${t.id}`)}
                    className="text-xs bg-primary hover:bg-primary text-primary-foreground rounded-full px-3 py-1.5 inline-flex items-center gap-1"
                    data-testid={`template-edit-${t.id}`}>
                    <Pencil className="w-3 h-3" /> Editar
                  </button>
                  <button onClick={() => duplicate(t)}
                    className="text-xs bg-card border border-border text-muted-foreground hover:text-primary rounded-full px-3 py-1.5 inline-flex items-center gap-1"
                    data-testid={`template-duplicate-${t.id}`}>
                    <Copy className="w-3 h-3" /> Duplicar
                  </button>
                  {!t.is_default && (
                    <>
                      <button onClick={() => setDefault(t.id)}
                        className="text-xs bg-card border border-border text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1"
                        data-testid={`template-set-default-${t.id}`}>
                        <Check className="w-3 h-3" /> Tornar default
                      </button>
                      <button onClick={() => remove(t)}
                        className="text-xs bg-card border border-[hsl(var(--danger))]/25 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))] rounded-full px-3 py-1.5 inline-flex items-center gap-1"
                        data-testid={`template-delete-${t.id}`}>
                        <Trash2 className="w-3 h-3" /> Eliminar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showNew && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowNew(false)}>
            <div className="bg-card rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="new-template-dialog">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-1">Novo template</div>
              <h2 className="font-display text-2xl text-primary mb-6">Criar template de proposta</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Nome *</label>
                  <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="Ex: Caring Select Premium"
                    className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    data-testid="new-template-name" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Descrição (opcional)</label>
                  <textarea rows={2} value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                    placeholder="Para que casos usar este template?"
                    className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y"
                    data-testid="new-template-desc" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Duplicar a partir de</label>
                  <select value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}
                    className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    data-testid="new-template-clone">
                    <option value="">Vazio (blocos default)</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-6">
                <button onClick={() => setShowNew(false)} className="text-sm px-4 py-2 rounded-lg text-muted-foreground hover:bg-background">Cancelar</button>
                <button onClick={createNew} disabled={creating || !newName.trim()}
                  className="text-sm px-5 py-2 rounded-lg bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="new-template-create">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Criar template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
